import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingPaintingContractorsCapeTownPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Painting Contractors Cape Town - Professional Commercial & Residential Painting Services',
    description: 'Leading painting contractors in Cape Town specializing in large-scale commercial, industrial, and residential projects. Expert project management, quality workmanship, and competitive pricing with over 25 years of experience serving the Western Cape.',
    keywords: 'painting contractors Cape Town, commercial painting contractors, industrial painting, residential contractors, large scale painting, project management, Cape Town contractors',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painting Contractors Cape Town',
          description: 'Professional painting contractors for commercial and residential projects',
          address: 'Cape Town, Western Cape',
          priceRange: '$$$',
          serviceArea: ['Cape Town', 'Western Cape', 'Southern Suburbs', 'Northern Suburbs', 'City Bowl']
        }),
        generateServiceSchema({
          name: 'Large Scale Painting Solutions',
          description: 'Commercial, industrial, and residential painting contractor services',
          provider: 'Painting Contractors Cape Town',
          areaServed: ['Cape Town', 'Western Cape'],
          serviceType: ['Commercial Contracting', 'Industrial Painting', 'Residential Projects']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painting Contractors Cape Town",
    location: "Cape Town, Western Cape",
    website: "https://www.paintingcontractorscapetown.com/",
    heroImage: "https://public.readdy.ai/ai/img_res/150de0f8cbe62b491b4a1dd877759dc2.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=massive%20commercial%20building%20exterior%20painting%20cape%20town%20cbd%20multiple%20story%20structure%20professional%20contractors%20working%20systematic%20approach%20urban%20setting&width=800&height=600&seq=gallery-pcc1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=industrial%20warehouse%20painting%20project%20cape%20town%20large%20facility%20professional%20application%20specialized%20equipment%20commercial%20coating%20systems&width=800&height=600&seq=gallery-pcc2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=luxury%20residential%20estate%20painting%20cape%20town%20multiple%20buildings%20coordinated%20project%20management%20professional%20finish%20mountain%20backdrop&width=800&height=600&seq=gallery-pcc3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=shopping%20center%20exterior%20painting%20cape%20town%20retail%20complex%20professional%20contractors%20systematic%20approach%20large%20scale%20project%20modern%20commercial%20architecture&width=800&height=600&seq=gallery-pcc4&orientation=landscape"
    ],
    services: [
      {
        title: "Commercial Painting Contracting",
        description: "Comprehensive commercial painting contracting services for large-scale projects throughout Cape Town and the Western Cape. We specialize in office buildings, retail complexes, shopping centers, hotels, hospitals, schools, and government facilities. Our experienced project management team ensures efficient execution with minimal disruption to your business operations while maintaining the highest quality standards.",
        features: ["Multi-story office buildings", "Shopping centers and malls", "Hotels and hospitality venues", "Hospitals and healthcare facilities", "Educational institutions", "Government buildings", "Retail chain stores", "Corporate headquarters", "Mixed-use developments", "Strata and body corporate projects", "Project management services", "Phased construction coordination"]
      },
      {
        title: "Industrial Painting Solutions",
        description: "Specialized industrial painting services for manufacturing facilities, warehouses, factories, and heavy industry throughout the Western Cape. We understand the unique requirements of industrial environments including chemical resistance, safety compliance, and operational continuity. Our team is trained in advanced coating systems, safety protocols, and works with minimal production disruption.",
        features: ["Manufacturing facilities", "Industrial warehouses", "Chemical processing plants", "Food processing facilities", "Automotive facilities", "Marine and port facilities", "Power generation plants", "Oil and gas installations", "Mining operations", "Specialized coating systems", "Safety compliance certified", "Environmental regulations adherent"]
      },
      {
        title: "Large Residential Projects",
        description: "Expert painting services for large residential developments, luxury estates, apartment complexes, and townhouse developments. We manage multi-unit projects with systematic coordination ensuring consistent quality across all properties. Our team handles everything from initial planning through final completion with comprehensive project oversight and quality assurance.",
        features: ["Residential developments", "Luxury estate complexes", "Apartment buildings", "Townhouse developments", "Retirement villages", "Student accommodation", "Holiday resorts", "Gated communities", "Heritage restoration projects", "Multi-phase developments", "Coordinated scheduling", "Quality consistency management"]
      }
    ],
    serviceAreas: ["Cape Town", "Western Cape", "Southern Suburbs", "Northern Suburbs", "City Bowl", "Atlantic Seaboard", "Stellenbosch", "Paarl", "Somerset West", "Strand", "Bellville", "Durbanville", "Goodwood", "Parow", "Brackenfell", "Kuils River", "Eerste River", "Kraaifontein"],
    about: "Painting Contractors Cape Town is the premier painting contracting company serving large-scale commercial, industrial, and residential projects throughout the Western Cape region. With over 25 years of industry leadership and a team of highly experienced project managers, supervisors, and skilled painters, we have successfully completed some of the most significant painting projects in Cape Town and surrounding areas. Our reputation is built on exceptional project management, superior craftsmanship, safety excellence, and unwavering commitment to client satisfaction.",
    aboutExtended: [
      "Established in 1998, our company has evolved from a regional painting contractor to become one of the most trusted and capable painting contracting firms in South Africa. We have built our reputation by successfully completing challenging, large-scale projects that require advanced technical expertise, comprehensive project management, and strict adherence to safety and quality standards. Our portfolio includes iconic commercial buildings, major industrial facilities, and prestigious residential developments throughout the Western Cape.",
      "Our team structure is designed to handle projects of any scale and complexity. We employ certified project managers, experienced site supervisors, qualified safety officers, and skilled painters who work together to deliver exceptional results. Every team member undergoes extensive training in advanced painting techniques, safety procedures, quality control, and customer service. We maintain ongoing certification in industry safety standards, environmental compliance, and specialized coating applications.",
      "We exclusively partner with leading paint manufacturers and coating specialists to ensure access to the most advanced products and technologies available. Our relationships with suppliers like Dulux, Plascon, Prominent Paints, and international specialty coating manufacturers enable us to specify the optimal materials for each project's unique requirements. We stay current with industry innovations, sustainable practices, and emerging technologies through continuous research and development partnerships.",
      "Our comprehensive approach to project management sets us apart from traditional painting companies. We begin every project with detailed planning, comprehensive site analysis, and thorough risk assessment. Our project management methodology includes detailed scheduling, resource allocation, quality control procedures, safety protocols, and regular client communication. We use advanced project management software to track progress, manage resources, and ensure on-time, on-budget completion of every project.",
      "Safety is paramount in all our operations. We maintain comprehensive safety management systems, conduct regular safety training, and ensure full compliance with all relevant safety regulations and industry standards. Our safety record is exemplary, and we are committed to maintaining a zero-incident workplace. We carry comprehensive insurance coverage including public liability, professional indemnity, and contractor's all-risk policies to protect our clients and our operations.",
      "Environmental responsibility is integral to our business practices. We use low-VOC and environmentally friendly paints wherever possible, implement waste reduction strategies, and ensure proper disposal of all materials in accordance with environmental regulations. We are committed to sustainable business practices and continuously seek to minimize our environmental impact while delivering superior results for our clients."
    ],
    ourApproach: [
      "Comprehensive project assessment and planning",
      "Detailed technical specifications development",
      "Advanced project management methodology",
      "Systematic quality control procedures",
      "Comprehensive safety management systems",
      "Coordinated scheduling and resource allocation",
      "Regular progress monitoring and reporting",
      "Strict adherence to timelines and budgets",
      "Professional client communication protocols",
      "Thorough documentation and record keeping",
      "Final inspection and quality assurance",
      "Comprehensive warranty and maintenance programs",
      "Post-project support and service"
    ],
    whyChoose: [
      "25+ years of contracting experience",
      "Large-scale project specialists",
      "Comprehensive project management",
      "Certified professional team",
      "Advanced safety management",
      "Environmental compliance certified",
      "Premium quality materials",
      "Competitive contractor pricing",
      "Detailed project documentation",
      "Comprehensive insurance coverage",
      "Excellent safety record",
      "All Western Cape coverage",
      "Commercial and industrial expertise",
      "Multi-phase project coordination",
      "Quality assurance systems",
      "Professional client service",
      "Sustainable business practices",
      "Technology-driven processes"
    ],
    testimonials: [
      {
        name: "Mark Stevens",
        rating: 5,
        text: "Painting Contractors Cape Town managed our entire office complex renovation flawlessly. Their project management was exceptional, quality outstanding, and they completed everything on schedule. Professional team that delivered exactly what was promised. Highly recommended for large commercial projects!"
      },
      {
        name: "Rachel Thompson",
        rating: 5,
        text: "We hired them for our industrial facility painting and couldn't be more impressed. They understood our operational requirements, worked around our production schedule, and delivered superior results. Safety was excellent throughout and the finish quality is exceptional."
      },
      {
        name: "James Botha",
        rating: 5,
        text: "Outstanding contractor service for our residential development project. They coordinated multiple buildings simultaneously, maintained consistent quality across all units, and completed the entire project on time and within budget. Excellent communication and project management throughout."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painting Contractors Cape Town</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painting Contractors Cape Town</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We provide professional painting contracting services throughout the Western Cape:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Contracting Services</h2>
                <p className="text-gray-700 text-lg mb-8">Large-scale painting contracting solutions for commercial, industrial, and residential projects.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Cape Town & Western Cape</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 6:30AM-6:30PM</p>
                    <p className="text-gray-600">Sat: 7AM-3PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by leaderr
              </a>
              <span>•</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}